import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Check, ArrowRight, ArrowLeft, User, GraduationCap, CreditCard, CalendarCheck, ExternalLink, Video, Clock, BadgeCheck, Plus, Trash2, AlertCircle, Users, ArrowRightLeft } from 'lucide-react';
import { PaymentMethodSelector, ZelleInstructionsPanel } from '../components/PaymentMethodSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyStudent = () => ({ student_name: '', student_dob: '', student_grade: '', spanish_level_self: '' });

export default function NewEnrollmentPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [settings, setSettings] = useState({ fee_amount: 52, zoom_scheduler_url: '' });
  const [evalPayMethod, setEvalPayMethod] = useState('card');
  const [evalMethodPct, setEvalMethodPct] = useState(0);
  const [paySettings, setPaySettings] = useState(null);

  const [parentData, setParentData] = useState({
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    parent_address: ''
  });

  const [students, setStudents] = useState([emptyStudent()]);

  useEffect(() => { fetchSettings(); fetchPaySettings(); checkPaymentReturn(); }, []);

  // Pre-fill parent info from authenticated user
  useEffect(() => {
    if (user && !parentData.parent_email) {
      setParentData(prev => ({
        ...prev,
        parent_name: user.name || prev.parent_name,
        parent_email: user.email || prev.parent_email,
        parent_phone: user.phone || prev.parent_phone,
        parent_address: user.address || prev.parent_address
      }));
    }
  }, [user]);

  const checkPaymentReturn = () => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    if (paymentStatus === 'success') {
      setStep(4);
      setEvaluationResult({ status: 'paid_awaiting_appointment', message: 'Payment confirmed' });
      toast.success(language === 'es' ? 'Pago confirmado — reserva tu cita de evaluación' : 'Payment confirmed — book your assessment appointment');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'canceled') {
      toast.error(language === 'es' ? 'Pago cancelado. Puedes intentar de nuevo.' : 'Payment canceled. You can try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/evaluation-settings/public`);
      setSettings(res.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchPaySettings = async () => {
    try {
      const res = await axios.get(`${API}/payment-settings/public`);
      setPaySettings(res.data);
    } catch (e) { /* fallback defaults */ }
  };

  const handleParentChange = (e) => {
    setParentData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const updateStudent = (index, field, value) => {
    setStudents(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const addStudent = () => {
    setStudents(prev => [...prev, emptyStudent()]);
  };

  const removeStudent = (index) => {
    if (students.length <= 1) return;
    setStudents(prev => prev.filter((_, i) => i !== index));
  };

  const validateStep = () => {
    if (step === 1) {
      if (!parentData.parent_name || !parentData.parent_email || !parentData.parent_phone || !parentData.parent_address) {
        toast.error(language === 'es' ? 'Por favor complete todos los campos' : 'Please fill all fields');
        return false;
      }
      if (!/\S+@\S+\.\S+/.test(parentData.parent_email)) {
        toast.error(language === 'es' ? 'Email inválido' : 'Invalid email');
        return false;
      }
    }
    if (step === 2) {
      for (let i = 0; i < students.length; i++) {
        const s = students[i];
        if (!s.student_name || !s.student_dob || !s.student_grade || !s.spanish_level_self) {
          toast.error(language === 'es'
            ? `Por favor complete todos los campos del alumno ${i + 1}`
            : `Please fill all fields for student ${i + 1}`);
          return false;
        }
      }
    }
    return true;
  };

  const nextStep = () => { if (validateStep()) setStep(prev => prev + 1); };
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  const handlePayEvaluation = async () => {
    setLoading(true);
    try {
      const payload = {
        ...parentData,
        payment_method: evalPayMethod,
        students: students.map(s => ({
          student_name: s.student_name,
          student_dob: s.student_dob,
          student_grade: s.student_grade,
          spanish_level_self: s.spanish_level_self
        }))
      };
      const res = await axios.post(`${API}/evaluations`, payload);
      
      // If Stripe checkout URL returned, redirect to Stripe
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      
      // Otherwise (Zelle or fallback), show success step
      setEvaluationResult(res.data);
      setStep(4);
      if (res.data.payment_method === 'zelle') {
        toast.success(language === 'es' ? 'Inscripción registrada — pendiente pago Zelle' : 'Registration submitted — pending Zelle payment');
      } else {
        toast.success(language === 'es' ? 'Pago recibido' : 'Payment received');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const feeAmount = settings.fee_amount || 52;
  const totalFee = feeAmount * students.length;
  const discountedTotal = evalMethodPct > 0 ? Math.round(totalFee * (1 - evalMethodPct / 100) * 100) / 100 : totalFee;
  const evalMethodLabel = evalPayMethod === 'ach' ? 'ACH' : evalPayMethod === 'zelle' ? 'Zelle' : '';

  const spanishLevels = [
    { value: 'none', label: language === 'es' ? 'Ninguno' : 'None' },
    { value: 'basic', label: language === 'es' ? 'Básico' : 'Basic' },
    { value: 'intermediate', label: language === 'es' ? 'Intermedio' : 'Intermediate' },
    { value: 'advanced', label: language === 'es' ? 'Avanzado' : 'Advanced' }
  ];

  const grades = [
    { value: 'preschool', label: language === 'es' ? 'Preescolar' : 'Preschool' },
    { value: 'kindergarten', label: 'Kindergarten' },
    ...Array.from({ length: 12 }, (_, i) => ({
      value: `${i + 1}`, label: language === 'es' ? `${i + 1}° grado` : `${i + 1}${['st', 'nd', 'rd'][i] || 'th'} Grade`
    }))
  ];

  const stepLabel = step === 3
    ? (language === 'es' ? `Evaluación ($${totalFee})` : `Assessment ($${totalFee})`)
    : (language === 'es' ? `Evaluación ($${feeAmount})` : `Assessment ($${feeAmount})`);

  const stepsDef = [
    { icon: User, label: language === 'es' ? 'Datos del padre' : 'Parent Info' },
    { icon: GraduationCap, label: language === 'es' ? 'Datos del alumno' : 'Student Info' },
    { icon: CreditCard, label: stepLabel },
    { icon: CalendarCheck, label: language === 'es' ? 'Agendar cita' : 'Book Appointment' }
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <Navbar />
      <SEOHead
        titleES="Nueva Inscripción — Colegio ARCE"
        titleEN="New Enrollment — Colegio ARCE"
        descES="Inscribe a tu hijo en Colegio ARCE."
        descEN="Enroll your child at Colegio ARCE."
        path="/enrollment/new"
      />
      <main className="pt-40 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-semibold text-[#1D1D1F] mb-4" data-testid="new-enrollment-title">
              {language === 'es' ? 'Nueva Inscripción' : 'New Enrollment'}
            </h1>
            <div className="inline-flex items-start gap-2 p-3 bg-[#007AFF]/5 border border-[#007AFF]/20 rounded-xl max-w-lg">
              <AlertCircle className="h-4 w-4 text-[#007AFF] mt-0.5 shrink-0" />
              <p className="text-sm text-[#1D1D1F] leading-relaxed text-left" data-testid="eval-mandatory-note">
                {language === 'es'
                  ? 'Todos los nuevos alumnos deben realizar una evaluación de nivel antes de completar la inscripción.'
                  : 'All new students must complete a level assessment before finalizing enrollment.'}
              </p>
            </div>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-12">
            {stepsDef.map((s, index) => (
              <div key={index} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  step > index + 1 ? 'bg-[#34C759] text-white' :
                  step === index + 1 ? 'bg-[#E63946] text-white shadow-lg' : 'bg-black/5 text-[#86868B]'
                }`} data-testid={`eval-step-indicator-${index + 1}`}>
                  {step > index + 1 ? <Check className="h-5 w-5" /> : <s.icon className="h-5 w-5" />}
                </div>
                {index < stepsDef.length - 1 && (
                  <div className={`w-8 sm:w-14 h-0.5 mx-1 ${step > index + 1 ? 'bg-[#34C759]' : 'bg-black/10'}`} />
                )}
              </div>
            ))}
          </div>

          <div className="card-apple p-8">
            {/* Step 1: Parent Info */}
            {step === 1 && (
              <div className="space-y-6" data-testid="eval-step-1">
                <h2 className="text-xl font-semibold text-[#1D1D1F] mb-6">
                  {language === 'es' ? 'Información del padre/tutor' : 'Parent/Guardian Information'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Nombre completo' : 'Full Name'}</Label>
                    <Input name="parent_name" value={parentData.parent_name} onChange={handleParentChange} className="py-6 rounded-xl" data-testid="eval-parent-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Correo electrónico' : 'Email'}</Label>
                    <Input name="parent_email" type="email" value={parentData.parent_email} onChange={handleParentChange} className="py-6 rounded-xl" data-testid="eval-parent-email" />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Teléfono' : 'Phone'}</Label>
                    <Input name="parent_phone" type="tel" value={parentData.parent_phone} onChange={handleParentChange} className="py-6 rounded-xl" data-testid="eval-parent-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Dirección' : 'Address'}</Label>
                    <Input name="parent_address" value={parentData.parent_address} onChange={handleParentChange} className="py-6 rounded-xl" data-testid="eval-parent-address" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Student Info (Multi-child) */}
            {step === 2 && (
              <div className="space-y-6" data-testid="eval-step-2">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-[#1D1D1F]">
                    {language === 'es' ? 'Información del alumno' : 'Student Information'}
                  </h2>
                  {students.length > 1 && (
                    <span className="text-sm text-[#007AFF] font-medium" data-testid="student-count-badge">
                      {students.length} {language === 'es' ? 'alumnos' : 'students'}
                    </span>
                  )}
                </div>

                {students.map((student, idx) => (
                  <div key={idx} className={`space-y-4 ${idx > 0 ? 'pt-6 border-t border-black/5' : ''}`} data-testid={`student-form-${idx}`}>
                    {students.length > 1 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-[#E63946]">
                          {language === 'es' ? `Alumno ${idx + 1}` : `Student ${idx + 1}`}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg h-8 px-2"
                          onClick={() => removeStudent(idx)}
                          data-testid={`remove-student-${idx}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          {language === 'es' ? 'Eliminar' : 'Remove'}
                        </Button>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{language === 'es' ? 'Nombre del alumno' : 'Student Name'}</Label>
                        <Input value={student.student_name} onChange={e => updateStudent(idx, 'student_name', e.target.value)} className="py-6 rounded-xl" data-testid={`eval-student-name-${idx}`} />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'es' ? 'Fecha de nacimiento' : 'Date of Birth'}</Label>
                        <Input type="date" value={student.student_dob} onChange={e => updateStudent(idx, 'student_dob', e.target.value)} className="py-6 rounded-xl" data-testid={`eval-student-dob-${idx}`} />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'es' ? 'Grado escolar' : 'Grade'}</Label>
                        <Select value={student.student_grade} onValueChange={v => updateStudent(idx, 'student_grade', v)}>
                          <SelectTrigger className="py-6 rounded-xl" data-testid={`eval-student-grade-${idx}`}>
                            <SelectValue placeholder={language === 'es' ? 'Seleccionar' : 'Select'} />
                          </SelectTrigger>
                          <SelectContent>
                            {grades.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'es' ? 'Nivel de español (autoevaluación)' : 'Spanish Level (self-assessed)'}</Label>
                        <Select value={student.spanish_level_self} onValueChange={v => updateStudent(idx, 'spanish_level_self', v)}>
                          <SelectTrigger className="py-6 rounded-xl" data-testid={`eval-spanish-level-${idx}`}>
                            <SelectValue placeholder={language === 'es' ? 'Seleccionar' : 'Select'} />
                          </SelectTrigger>
                          <SelectContent>
                            {spanishLevels.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add another child button */}
                <Button
                  variant="outline"
                  onClick={addStudent}
                  className="w-full rounded-xl border-dashed border-2 border-[#007AFF]/30 text-[#007AFF] hover:bg-[#007AFF]/5 hover:border-[#007AFF]/50 py-5"
                  data-testid="add-student-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Añadir otro hijo/a' : 'Add another child'}
                </Button>

                {/* Evaluation Note */}
                <div className="p-4 bg-[#007AFF]/5 border border-[#007AFF]/20 rounded-xl">
                  <p className="text-sm text-[#1D1D1F] leading-relaxed">
                    <strong>{language === 'es' ? 'Nota:' : 'Note:'}</strong>{' '}
                    {language === 'es'
                      ? 'El nivel seleccionado es solo informativo. El nivel real será determinado por nuestra directora académica durante la evaluación.'
                      : 'The selected level is informational only. The actual level will be determined by our academic director during the assessment.'}
                  </p>
                </div>
              </div>
            )}

            {/* Step 3: Evaluation Fee Payment */}
            {step === 3 && (
              <div className="space-y-6" data-testid="eval-step-3">
                <h2 className="text-xl font-semibold text-[#1D1D1F] mb-2">
                  {language === 'es' ? 'Reserva tu evaluación de nivel' : 'Book your level assessment'}
                </h2>
                <p className="text-sm text-[#86868B] leading-relaxed">
                  {language === 'es'
                    ? `Para evaluar el nivel de tu hijo/a, realizamos una sesión individual por videollamada con nuestra directora académica. El coste de la evaluación es de $${feeAmount} por alumno, que se descontará del importe de la matrícula si decides inscribir a tu hijo/a.`
                    : `To assess your child's level, we conduct an individual video call session with our academic director. The evaluation fee is $${feeAmount} per student, which will be deducted from the tuition if you decide to enroll your child.`}
                </p>

                {/* Benefits */}
                <div className="space-y-3 py-4">
                  {[
                    { icon: Video, textES: 'Sesión individual de 30 minutos por Zoom', textEN: '30-minute individual Zoom session' },
                    { icon: Clock, textES: 'Resultado y recomendación de nivel en 24–48h', textEN: 'Level result and recommendation within 24–48h' },
                    { icon: BadgeCheck, textES: `$${feeAmount} descontados de la matrícula si te inscribes`, textEN: `$${feeAmount} deducted from tuition if you enroll` }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#34C759]/10 flex items-center justify-center shrink-0">
                        <item.icon className="h-4 w-4 text-[#34C759]" />
                      </div>
                      <p className="text-sm text-[#1D1D1F]">{language === 'es' ? item.textES : item.textEN}</p>
                    </div>
                  ))}
                </div>

                {/* Payment Summary - Itemized per child */}
                <div className="p-6 bg-[#F5F5F7] rounded-2xl space-y-3" data-testid="eval-payment-summary">
                  {students.map((s, i) => (
                    <div key={i} className="flex items-center justify-between py-2" data-testid={`eval-fee-line-${i}`}>
                      <div className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-[#86868B]" />
                        <span className="text-sm text-[#1D1D1F]">{s.student_name}</span>
                      </div>
                      <span className="text-sm font-medium text-[#1D1D1F]">
                        {language === 'es' ? 'Evaluación' : 'Assessment'}: ${feeAmount}
                      </span>
                    </div>
                  ))}
                  {students.length > 1 && <div className="border-t border-black/10" />}
                  <div className="flex items-center justify-between">
                    <span className="text-[#86868B] font-medium">Total</span>
                    <span className={`text-3xl font-bold text-[#1D1D1F] ${evalMethodPct > 0 ? 'line-through opacity-40 text-xl' : ''}`} data-testid="eval-fee-total">${totalFee}</span>
                  </div>
                  {evalMethodPct > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#34C759] font-medium text-sm">{language === 'es' ? `Con descuento ${evalMethodLabel}` : `With ${evalMethodLabel} discount`}</span>
                      <span className="text-3xl font-bold text-[#34C759]" data-testid="eval-fee-discounted">${discountedTotal}</span>
                    </div>
                  )}
                </div>

                {/* Payment Method Selector */}
                <PaymentMethodSelector
                  amount={totalFee}
                  language={language}
                  onMethodChange={(method, pct) => {
                    setEvalPayMethod(method);
                    setEvalMethodPct(pct);
                  }}
                  showComparison={true}
                />

                {/* Zelle instructions for eval */}
                {evalPayMethod === 'zelle' && (
                  <ZelleInstructionsPanel
                    amount={discountedTotal}
                    studentNames={students.map(s => s.student_name).join(', ')}
                    language={language}
                    settings={paySettings}
                  />
                )}

                <div className="space-y-3">
                  <Button
                    onClick={handlePayEvaluation}
                    disabled={loading}
                    className={`w-full py-6 rounded-xl text-base font-semibold ${evalPayMethod === 'zelle' ? 'bg-[#6B21A8] hover:bg-[#5B1A98]' : 'bg-[#E63946] hover:bg-[#D62828]'}`}
                    data-testid="pay-evaluation-btn"
                  >
                    {evalPayMethod === 'zelle'
                      ? <ArrowRightLeft className="h-5 w-5 mr-2" />
                      : <CreditCard className="h-5 w-5 mr-2" />}
                    {loading
                      ? (language === 'es' ? 'Procesando...' : 'Processing...')
                      : evalPayMethod === 'zelle'
                        ? (language === 'es' ? `Pagar $${discountedTotal} y agendar evaluación` : `Pay $${discountedTotal} & book assessment`)
                        : (language === 'es' ? `Pagar $${evalMethodPct > 0 ? discountedTotal : totalFee} y agendar evaluación` : `Pay $${evalMethodPct > 0 ? discountedTotal : totalFee} & book assessment`)}
                  </Button>
                </div>

                <div className="text-center space-y-1">
                  <p className="text-sm font-bold text-[#007AFF]">
                    {language === 'es'
                      ? `Los $${feeAmount} se descontarán del precio de la matrícula si te inscribes.`
                      : `The $${feeAmount} will be deducted from tuition if you enroll.`}
                  </p>
                  <p className="text-xs text-[#E63946] font-medium">
                    {language === 'es'
                      ? `Este monto no es reembolsable en caso de no inscripción.`
                      : `This fee is non-refundable if you do not enroll.`}
                  </p>
                </div>
              </div>
            )}

            {/* Step 4: Booking Confirmation */}
            {step === 4 && (
              <div className="space-y-6" data-testid="eval-step-4">
                {/* Success Banner */}
                <div className="text-center p-6 bg-[#34C759]/5 border border-[#34C759]/20 rounded-2xl">
                  <div className="w-16 h-16 rounded-full bg-[#34C759] flex items-center justify-center mx-auto mb-4">
                    <Check className="h-8 w-8 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#1D1D1F] mb-2" data-testid="payment-success-title">
                    {language === 'es' ? '¡Pago recibido!' : 'Payment received!'}
                  </h2>
                  <p className="text-sm text-[#86868B]">
                    {language === 'es'
                      ? 'Ahora agenda tu evaluación con nuestra directora académica.'
                      : 'Now book your assessment with our academic director.'}
                  </p>
                </div>

                {/* ── Timeline: Próximos Pasos ── */}
                <div className="p-6 bg-white border border-black/5 rounded-2xl" data-testid="next-steps-timeline">
                  <h3 className="text-base font-semibold text-[#1D1D1F] mb-5">
                    {language === 'es' ? 'Próximos pasos' : 'Next steps'}
                  </h3>
                  <div className="space-y-0">
                    {[
                      { icon: <Check className="h-4 w-4" />, color: 'bg-[#34C759]', title: language === 'es' ? 'Pago de evaluación' : 'Evaluation payment', desc: language === 'es' ? `$${feeAmount} recibidos — se descontarán de la matrícula` : `$${feeAmount} received — will be deducted from tuition`, done: true },
                      { icon: <Video className="h-4 w-4" />, color: 'bg-[#007AFF]', title: language === 'es' ? 'Agendar evaluación' : 'Book assessment', desc: language === 'es' ? '30 min por Zoom con nuestra directora académica' : '30-min Zoom session with our academic director', done: false, active: true },
                      { icon: <GraduationCap className="h-4 w-4" />, color: 'bg-[#86868B]', title: language === 'es' ? 'Resultado y nivel asignado' : 'Result & level assigned', desc: language === 'es' ? 'Recibirás el resultado por email en 24-48h' : 'You will receive the result by email within 24-48h', done: false },
                      { icon: <CreditCard className="h-4 w-4" />, color: 'bg-[#86868B]', title: language === 'es' ? 'Finalizar inscripción y pagar' : 'Complete enrollment & pay', desc: language === 'es' ? 'Elige tu plan y completa la inscripción' : 'Choose your plan and complete enrollment', done: false },
                      { icon: <CalendarCheck className="h-4 w-4" />, color: 'bg-[#86868B]', title: language === 'es' ? '¡Bienvenido a Colegio ARCE!' : 'Welcome to Colegio ARCE!', desc: language === 'es' ? 'Recibirás horarios y materiales antes del inicio' : 'You will receive schedules and materials before start', done: false },
                    ].map((step, i, arr) => (
                      <div key={i} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0 ${step.done ? 'bg-[#34C759]' : step.active ? 'bg-[#007AFF] ring-4 ring-[#007AFF]/20' : 'bg-[#D1D1D6]'}`}>
                            {step.icon}
                          </div>
                          {i < arr.length - 1 && <div className={`w-0.5 h-10 ${step.done ? 'bg-[#34C759]' : 'bg-[#E5E5EA]'}`} />}
                        </div>
                        <div className="pb-8">
                          <p className={`text-sm font-semibold ${step.done ? 'text-[#34C759]' : step.active ? 'text-[#007AFF]' : 'text-[#86868B]'}`}>
                            {step.title} {step.done && '✓'}
                          </p>
                          <p className="text-xs text-[#86868B] mt-0.5">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Multi-child booking notice */}
                {students.length > 1 && (
                  <div className="p-4 bg-[#FF9500]/5 border border-[#FF9500]/20 rounded-xl flex items-start gap-3" data-testid="multi-child-booking-notice">
                    <Users className="h-5 w-5 text-[#FF9500] mt-0.5 shrink-0" />
                    <p className="text-sm text-[#1D1D1F]">
                      {language === 'es'
                        ? `Necesitas agendar una evaluación por cada hijo/a (${students.length} evaluaciones en total).`
                        : `You need to book one assessment per child (${students.length} assessments total).`}
                    </p>
                  </div>
                )}

                {/* Booking section per child */}
                {students.map((s, i) => (
                  <div key={i} className="p-5 border border-black/5 rounded-2xl space-y-3" data-testid={`booking-child-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#E63946]/10 flex items-center justify-center text-sm font-semibold text-[#E63946]">
                        {i + 1}
                      </div>
                      <div>
                        <p className="font-medium text-[#1D1D1F]">{s.student_name}</p>
                        <p className="text-xs text-[#86868B]">
                          {language === 'es' ? 'Evaluación individual de 30 min' : '30-min individual assessment'}
                        </p>
                      </div>
                    </div>
                    {settings.zoom_scheduler_url && (
                      <a
                        href={settings.zoom_scheduler_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 p-3 bg-[#007AFF]/5 border border-[#007AFF]/20 rounded-xl hover:border-[#007AFF]/40 transition-colors text-[#007AFF] text-sm font-medium"
                        data-testid={`zoom-link-${i}`}
                      >
                        <Video className="h-4 w-4" />
                        {language === 'es' ? `Agendar evaluación de ${s.student_name}` : `Book assessment for ${s.student_name}`}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                ))}

                {!settings.zoom_scheduler_url && (
                  <div className="p-6 bg-[#FF9500]/5 border border-[#FF9500]/20 rounded-2xl text-center">
                    <p className="text-sm text-[#1D1D1F]">
                      {language === 'es'
                        ? 'El enlace de reserva de citas no está configurado aún. Por favor contáctenos directamente.'
                        : 'The appointment booking link is not configured yet. Please contact us directly.'}
                    </p>
                  </div>
                )}

                {/* Help section */}
                <div className="p-4 bg-[#F5F5F7] rounded-xl">
                  <p className="text-sm text-[#86868B]">
                    {language === 'es'
                      ? '¿Problemas para agendar? Escríbenos por '
                      : 'Trouble booking? Contact us via '}
                    <a href="https://wa.me/13392277556" className="text-[#34C759] font-medium hover:underline">WhatsApp</a>
                    {language === 'es' ? ' o ' : ' or '}
                    <a href="mailto:laura@colegioarce.com" className="text-[#E63946] font-medium hover:underline">email</a>.
                  </p>
                </div>

                {/* Summary */}
                <div className="p-4 border border-black/5 rounded-xl space-y-2">
                  <p className="text-xs text-[#86868B] uppercase tracking-wider font-medium">
                    {language === 'es' ? 'Resumen' : 'Summary'}
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#86868B]">{language === 'es' ? 'Padre/Tutor' : 'Parent'}</span>
                      <span className="text-[#1D1D1F] font-medium">{parentData.parent_name}</span>
                    </div>
                    {students.map((s, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="text-[#86868B]">{language === 'es' ? `Alumno ${i + 1}` : `Student ${i + 1}`}</span>
                        <span className="text-[#1D1D1F] font-medium">{s.student_name}</span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-1 border-t border-black/5">
                      <span className="text-[#86868B]">{language === 'es' ? 'Pago total' : 'Total Payment'}</span>
                      <span className="text-[#34C759] font-semibold">${totalFee} — {language === 'es' ? 'Confirmado' : 'Confirmed'}</span>
                    </div>
                    {evaluationResult && (
                      <div className="flex justify-between">
                        <span className="text-[#86868B]">{language === 'es' ? 'Ref. familia' : 'Family ref.'}</span>
                        <span className="text-[#1D1D1F] font-mono text-xs">{evaluationResult.family_id}</span>
                      </div>
                    )}
                  </div>
                </div>

                <p className="text-xs text-[#86868B] text-center">
                  {language === 'es'
                    ? 'Recibirás un correo de confirmación con estos detalles y el enlace para agendar.'
                    : 'You will receive a confirmation email with these details and the booking link.'}
                </p>
                <p className="text-sm text-center font-bold text-[#007AFF]">
                  {language === 'es'
                    ? `Los $${feeAmount} de la evaluación se descontarán del importe total de la matrícula si decides inscribir a tu hijo/a.`
                    : `The $${feeAmount} evaluation fee will be deducted from the total tuition if you decide to enroll your child.`}
                </p>
              </div>
            )}

            {/* Navigation */}
            {step < 4 && (
              <div className="flex justify-between mt-8 pt-6 border-t border-black/5">
                <Button
                  variant="outline"
                  onClick={prevStep}
                  disabled={step === 1}
                  className="rounded-xl px-6"
                  data-testid="eval-back-btn"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Atrás' : 'Back'}
                </Button>
                {step < 3 ? (
                  <Button
                    onClick={nextStep}
                    className="rounded-xl px-6 bg-[#E63946] hover:bg-[#D62828]"
                    data-testid="eval-next-btn"
                  >
                    {language === 'es' ? 'Siguiente' : 'Next'}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
